import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

// MB-1454: the app had NO error boundary anywhere, so any uncaught render
// throw unmounted the entire React tree — a literally blank white page at a
// stable URL (prod repro: campaign wizard vs poison postTimes). This boundary
// wraps each layout's <Outlet/> so a crash degrades to a visible card with a
// way out, and the crash itself stays in the console for diagnosis.

const Fallback = ({ onReset }: { onReset: () => void }) => {
    const navigate = useNavigate();
    return (
        <main className="flex flex-col items-center justify-center min-h-[60vh] px-5 text-center">
            <h1 className="text-primary text-2xl font-bold mb-2">
                Something went wrong loading this page
            </h1>
            <p className="text-gray-600 mb-6 max-w-md">
                The error has been logged. You can go back and keep working -
                if this keeps happening on the same page, please let support
                know what you clicked.
            </p>
            <div className="flex gap-3">
                <button
                    className="px-4 py-2 rounded-lg bg-primary text-white"
                    onClick={() => {
                        onReset();
                        navigate("/listings");
                    }}
                >
                    Back to Listings
                </button>
                <button
                    className="px-4 py-2 rounded-lg border border-primary text-primary"
                    onClick={() => window.location.reload()}
                >
                    Reload
                </button>
            </div>
        </main>
    );
};

interface BoundaryState {
    hasError: boolean;
}

class Boundary extends React.Component<
    { children: React.ReactNode },
    BoundaryState
> {
    state: BoundaryState = { hasError: false };

    static getDerivedStateFromError(): BoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: unknown, info: React.ErrorInfo): void {
        console.error("PageErrorBoundary caught:", error, info.componentStack);
    }

    reset = (): void => this.setState({ hasError: false });

    render(): React.ReactNode {
        if (this.state.hasError) return <Fallback onReset={this.reset} />;
        return this.props.children;
    }
}

// Remount on every route change so navigating away from a crashed page
// automatically clears the error state. /add-post/<card> is a dialog over
// /add-post, not another page: a remount there refetched the page per card.
const pageKey = (pathname: string): string =>
    pathname.startsWith("/add-post/") ? "/add-post" : pathname;

const PageErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    return <Boundary key={pageKey(location.pathname)}>{children}</Boundary>;
};

export default PageErrorBoundary;
